// "Habits": manage habits (create, edit, order, pause) plus account and
// appearance settings.
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronRight, Download, GripVertical, LogOut, Monitor, Moon, Plus, Sun } from 'lucide-react';
import type { AppUser, Habit } from '../../shared/api';
import { scheduleLabel } from '../../shared/schedule';
import { useEditor } from '../components/EditorContext';
import { EmojiTile } from '../components/EmojiTile';
import { EmptyState } from '../components/EmptyState';
import { Link } from '../components/Link';
import { SkeletonList } from '../components/Skeleton';
import { useToast } from '../components/Toasts';
import { errorMessage } from '../lib/api';
import { promptInstall, useInstallAvailable } from '../lib/pwa';
import { paths } from '../lib/router';
import { reorderHabits, useStore } from '../lib/store';
import { setTheme, useTheme, type ThemeChoice } from '../lib/theme';

function HabitItem({ habit, sortable }: { habit: Habit; sortable: boolean }) {
  const editor = useEditor();
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: habit.id,
    disabled: !sortable,
  });
  return (
    <li
      ref={setNodeRef}
      className={`manage glass hc-${habit.color}${isDragging ? ' manage--dragging' : ''}${habit.archived ? ' manage--paused' : ''}`}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      {sortable && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          className="manage__grip"
          aria-label={`${habit.name} verschieben`}
          {...attributes}
          {...listeners}
        >
          <GripVertical size={18} />
        </button>
      )}
      <button type="button" className="manage__main" onClick={() => editor.openEdit(habit)}>
        <EmojiTile emoji={habit.emoji} sticker={habit.sticker} color={habit.color} size="sm" />
        <span className="manage__text">
          <span className="manage__name">{habit.name}</span>
          <span className="manage__meta">{habit.archived ? `Pausiert · ${scheduleLabel(habit.schedule)}` : scheduleLabel(habit.schedule)}</span>
        </span>
        <ChevronRight size={18} className="manage__chevron" aria-hidden="true" />
      </button>
    </li>
  );
}

const THEMES: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'light', label: 'Hell', icon: Sun },
  { value: 'dark', label: 'Dunkel', icon: Moon },
];

export function HabitsView({ user, onSignOut }: { user: AppUser; onSignOut: () => void }) {
  const { habits, loaded } = useStore();
  const editor = useEditor();
  const toast = useToast();
  const theme = useTheme();
  const installable = useInstallAvailable();
  const active = habits.filter((h) => !h.archived);
  const paused = habits.filter((h) => h.archived);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = ({ active: dragged, over }: DragEndEvent) => {
    if (!over || dragged.id === over.id) return;
    const ids = active.map((h) => h.id);
    const next = arrayMove(ids, ids.indexOf(String(dragged.id)), ids.indexOf(String(over.id)));
    reorderHabits([...next, ...paused.map((h) => h.id)]).catch((err) => toast({ text: errorMessage(err), kind: 'error' }));
  };

  return (
    <div className="view">
      <header className="page-head">
        <div className="page-head__text">
          <p className="eyebrow">Verwalten</p>
          <h1 className="page-title">Habits</h1>
          <p className="page-sub">Lege fest, was du wie oft tun möchtest.</p>
        </div>
        <button type="button" className="btn btn--primary page-head__action" onClick={() => editor.openNew()}>
          <Plus size={18} /> Neu
        </button>
      </header>

      {!loaded && <SkeletonList rows={3} />}
      {loaded && habits.length === 0 && <EmptyState />}

      {loaded && active.length > 0 && (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={active.map((h) => h.id)} strategy={verticalListSortingStrategy}>
              <ul className="manage-list" aria-label="Aktive Habits">
                {active.map((habit) => (
                  <HabitItem key={habit.id} habit={habit} sortable={active.length > 1} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
          {active.length > 1 && <p className="hint">Halte den Griff gedrückt und ziehe, um die Reihenfolge zu ändern.</p>}
        </>
      )}

      {loaded && paused.length > 0 && (
        <>
          <h2 className="section-title">Pausiert</h2>
          <ul className="manage-list">
            {paused.map((habit) => (
              <HabitItem key={habit.id} habit={habit} sortable={false} />
            ))}
          </ul>
        </>
      )}

      <h2 className="section-title">Einstellungen</h2>
      <section className="settings glass">
        <div className="settings__row">
          <div className="settings__label">
            <span className="settings__title">Darstellung</span>
          </div>
          <div className="segmented segmented--icons" role="radiogroup" aria-label="Darstellung">
            {THEMES.map(({ value, label, icon: Icon }) => (
              <button key={value} type="button" role="radio" aria-checked={theme === value} onClick={() => setTheme(value)}>
                <Icon size={16} aria-hidden="true" /> {label}
              </button>
            ))}
          </div>
        </div>
        {installable && (
          <div className="settings__row">
            <div className="settings__label">
              <span className="settings__title">App installieren</span>
              <span className="settings__sub">Auf dem Startbildschirm, ohne Browserleiste.</span>
            </div>
            <button type="button" className="btn btn--glass btn--sm" onClick={() => void promptInstall()}>
              <Download size={16} /> Installieren
            </button>
          </div>
        )}
        <div className="settings__row">
          <div className="settings__label">
            <span className="settings__title">Konto</span>
            <span className="settings__sub">Benutzerdaten und Passwort</span>
          </div>
          <Link to={paths.account} className="btn btn--glass btn--sm">
            Öffnen <ChevronRight size={16} aria-hidden="true" />
          </Link>
        </div>
        <div className="settings__row">
          <div className="settings__label">
            <span className="settings__title">Angemeldet</span>
            <span className="settings__sub settings__email">{user.email}</span>
          </div>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onSignOut}>
            <LogOut size={16} /> Abmelden
          </button>
        </div>
      </section>
    </div>
  );
}
